import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionSettings } from '@app/context/SessionSettingsContext'

import { useProfileRoutesConditions, useProfileRoutesRequired } from '@pages/Profile/ProfileRoutes'

import { useFeatureFlags } from '@context/FeatureFlagsContext'
import { useProductReadContext } from '@context/ProductContext'

import { AddFileIcon } from '@ui/icons/AddFile.icon'
import { BellIcon } from '@ui/icons/Bell.icon'
import { CardIcon } from '@ui/icons/Card.icon'
import { CircleCheckIcon } from '@ui/icons/CircleCheck.icon'
import { DepositIcon } from '@ui/icons/Deposit.icon'
import { EconomicProfileIcon } from '@ui/icons/EconomicProfile.icon'
import { EditFileIcon } from '@ui/icons/EditFile.icon'
import { FileIcon } from '@ui/icons/File.icon'
import { MarketDataClassificationIcon } from '@ui/icons/MarketDataClassification.icon'
import { MobileIcon } from '@ui/icons/Mobile.icon'
import { SettingIcon } from '@ui/icons/Setting.icon'
import { ShieldIcon } from '@ui/icons/Shield.icon'
import { UserIcon } from '@ui/icons/User.icon'
import { UserGroupIcon } from '@ui/icons/UserGroup.icon'
import { UserSquareIcon } from '@ui/icons/UserSquare.icon'

import { useNotificationUnread } from '@utils/notifications'
import { BasePaths, CommonPaths, getProductEconomicProfilePath } from '@utils/path'

import { useLanguageMenu } from '@shared/components/Navigation/menuSections/topLevelMenus/useLanguageMenu'

import { themeIconMap, useThemeOptions } from '../../v2/hooks/useThemeOptions'
import { MenuSection } from '../useMenuSections'

export const useProfileMenu = (): MenuSection => {
  const { t } = useTranslation()
  const { product } = useProductReadContext()
  const { darkMode } = useFeatureFlags()

  const {
    isFirstTimeDepositVisible,
    isAppropriatenessTestVisible,
    isEconomicProfileVisible,
    isClientClassificationVisible,
    isTaxFormVisible,
    isETaxFormVisible,
    isMarketDataClassificationVisible,
    isBankAccountVisible,
    isDocManagementVisible,
    isSumsubVisible,
  } = useProfileRoutesConditions()

  const { shouldVerifyPhoneNumber, appropriatenessTest, documentManagement } =
    useProfileRoutesRequired()

  const lang = useLanguageMenu()
  const themeOptions = useThemeOptions()

  const { hasUnread } = useNotificationUnread()

  const [{ theme }] = useSessionSettings()

  return useMemo(
    () => ({
      id: 'profile',
      label: t('Profile.Profile'),
      icon: UserIcon,
      children: [
        {
          label: t('Profile.Personal Details'),
          path: '/profile/personal-info',
          id: 'personal-info',
          icon: UserSquareIcon,
        },
        {
          label: t('Profile.Preferences'),
          path: '/profile/account-settings',
          id: 'account-settings',
          icon: SettingIcon,
        },
        {
          id: 'notifications',
          label: t('Notifications.Notifications'),
          path: `/profile/notifications`,
          alertActive: hasUnread,
          icon: BellIcon,
        },
        {
          label: t('Profile.Bank Accounts'),
          id: 'bank-accounts',
          path: '/profile/bank-accounts',
          condition: isBankAccountVisible,
          icon: CardIcon,
        },
        {
          label: t('Profile.Phone Numbers'),
          path: '/profile/phone-numbers',
          alertActive: shouldVerifyPhoneNumber,
          id: 'phone-numbers',
          icon: MobileIcon,
        },
        {
          label: t('Profile.Documents'),
          id: 'document-management',
          path: CommonPaths.DocumentManagement,
          alertActive: documentManagement,
          condition: isDocManagementVisible,
          icon: FileIcon,
        },
        {
          label: t('Profile.Security Settings'),
          path: '/profile/security-settings',
          id: 'security-settings',
          icon: ShieldIcon,
        },
        {
          label: t('Profile.Client Classification'),
          id: 'client-classification',
          path: '/profile/client-classification',
          condition: isClientClassificationVisible,
          icon: UserGroupIcon,
        },
        {
          label: t('Profile.Market Data Classification'),
          id: 'market-data-classification',
          path: '/profile/market-data-classification',
          condition: isMarketDataClassificationVisible,
          icon: MarketDataClassificationIcon,
        },
        {
          label: t('Profile.tax form'),
          id: 'tax-form',
          path: '/profile/tax-form',
          condition: isTaxFormVisible,
          icon: AddFileIcon,
        },

        {
          label: t('Profile.W-8BEN-e Form'),
          id: 'e-tax-form',
          path: '/profile/e-tax-form',
          condition: isETaxFormVisible,
          icon: AddFileIcon,
        },
        {
          id: 'first-time-deposit',
          label: t('Deposit'),
          path: CommonPaths.FirstTimeDeposit,
          alertActive: true,
          condition: isFirstTimeDepositVisible,
          icon: DepositIcon,
        },
        {
          label: t('Profile.Appropriateness Test'),
          id: 'appropriateness-test',
          path: BasePaths.TakeAppTest,
          alertActive: appropriatenessTest,
          condition: isAppropriatenessTestVisible,
          icon: EditFileIcon,
        },
        {
          label: t('Economic Profile'),
          id: 'economic-profile',
          path: getProductEconomicProfilePath(product),
          alertActive: isEconomicProfileVisible,
          condition: isEconomicProfileVisible,
          icon: EconomicProfileIcon,
        },

        {
          label: t('Verification'),
          id: 'verification',
          path: CommonPaths.Sumsub,
          alertActive: documentManagement,
          condition: isSumsubVisible,
          icon: CircleCheckIcon,
        },

        lang,
        {
          label: t('Theme.Theme'),
          id: 'profile-theme',
          icon: themeIconMap[theme],
          children: themeOptions,
          condition: darkMode,
          screenChildren: themeOptions,
        },
      ],
    }),
    [
      t,
      hasUnread,
      isBankAccountVisible,
      shouldVerifyPhoneNumber,
      documentManagement,
      isDocManagementVisible,
      isClientClassificationVisible,
      isMarketDataClassificationVisible,
      isTaxFormVisible,
      isETaxFormVisible,
      isFirstTimeDepositVisible,
      appropriatenessTest,
      isAppropriatenessTestVisible,
      product,
      isEconomicProfileVisible,
      isSumsubVisible,
      lang,
      theme,
      themeOptions,
      darkMode,
    ]
  )
}
